import { FC, useEffect, useRef } from 'react';
import { Box, Grid, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  useInventoryState,
  useInventoryActions,
  EViewState,
} from '../../contexts/inventory.context';
import InventoryGrid from './inventoryGrid.view';
import { TStatus } from '../../../../../apis/finance/finance.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  TInventoryItem,
  TInventoryCategory,
  TInventoryTransaction,
} from 'src/apis/inventory/inventory.api.interfaces';
import InventoryItemDetailsView from './subviews/details/inventoryItemDetails.view';
import InventoryCategoryDetailsView from './subviews/details/inventoryCategoryDetails.view';
import InventoryTransactionDetailsView from './subviews/details/inventoryTransactionDetails.view';
import { AddButton } from 'src/views/components/simple/AllButtons';
import AddEditInventoryView from './subviews/addEditInventory.view';

const InventoryView: FC = () => {
  const { translateText } = useTranslation();
  const {
    items,
    categories,
    transactions,
    isLoading,
    selectedItem,
    selectedCategory,
    selectedTransaction,
    viewState,
    currentViewType,
  } = useInventoryState();
  const {
    selectItem,
    selectCategory,
    selectTransaction,
    setViewState,
    setViewType,
    setModalOpen,
    setEditMode,
  } = useInventoryActions();
  const hasDataBeenFetched = useRef(false);

  useEffect(() => {
    if (!isLoading) {
      hasDataBeenFetched.current = true;
    }
  }, [isLoading]);

  if (isLoading) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="inventory-loading">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.inventory.loading')}
        </Typography>
      </Box>
    );
  }

  // Only show "no data" if data has been fetched and all arrays are empty
  if (
    hasDataBeenFetched.current &&
    !items.length &&
    !categories.length &&
    !transactions.length
  ) {
    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-testid="inventory-no-data">
        <Typography
          sx={{
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {translateText('administration.inventory.noData')}
        </Typography>
      </Box>
    );
  }

  const onItemsRowClick = (
    data: TInventoryItem | TInventoryCategory | TInventoryTransaction
  ) => {
    selectItem(data as TInventoryItem);
    setViewType('items');
    setViewState(EViewState.DETAILS);
  };

  const onCategoriesRowClick = (
    data: TInventoryItem | TInventoryCategory | TInventoryTransaction
  ) => {
    selectCategory(data as TInventoryCategory);
    setViewType('categories');
    setViewState(EViewState.DETAILS);
  };

  const onTransactionsRowClick = (
    data: TInventoryItem | TInventoryCategory | TInventoryTransaction
  ) => {
    selectTransaction(data as TInventoryTransaction);
    setViewType('transactions');
    setViewState(EViewState.DETAILS);
  };

  const handleBackToList = () => {
    setViewState(EViewState.LIST);
    setViewType(undefined);
    selectItem(undefined);
    selectCategory(undefined);
    selectTransaction(undefined);
  };

  const handleAddItemClickHandler = () => {
    setViewType('items');
    setEditMode('create');
    selectItem(undefined);
    selectCategory(undefined);
    selectTransaction(undefined);
    setModalOpen(true);
  };

  const renderDetailsView = () => {
    if (currentViewType === 'items' && selectedItem) {
      return <InventoryItemDetailsView />;
    }
    if (currentViewType === 'categories' && selectedCategory) {
      return <InventoryCategoryDetailsView />;
    }
    if (currentViewType === 'transactions' && selectedTransaction) {
      return <InventoryTransactionDetailsView />;
    }
    return null;
  };

  // Calculate summary statistics
  const totalItemsValue = items.reduce((sum, item) => sum + item.totalValue, 0);
  const lowStockItems = items.filter(
    (item) => item.quantity <= item.reorderPoint
  ).length;
  const activeCategories = categories.filter((cat) => cat.isActive).length;
  const pendingPayments = transactions.filter(
    (trans) => trans.paymentStatus === TStatus.UNPAID
  ).length;

  return (
    <Box
      sx={{ width: '100%', height: '100%', overflow: 'auto' }}
      data-testid="inventory-container"
    >
      {viewState === EViewState.DETAILS ? (
        <Box
          sx={{
            mb: { xs: 1, sm: 2 },
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1, sm: 0 },
          }}
          data-testid="inventory-details-header"
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToList}
            sx={{
              mb: { xs: 0, sm: 2 },
              minHeight: { xs: '44px', sm: '36px' },
              fontSize: { xs: '16px', sm: '14px' },
              width: { xs: '100%', sm: 'auto' },
            }}
            data-testid="back-to-list-button"
          >
            {translateText('administration.inventory.backToList')}
          </Button>
        </Box>
      ) : (
        <Box
          sx={{
            mb: { xs: 1, sm: 2 },
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
          }}
          data-testid="inventory-details-header"
        >
          <AddButton
            label={translateText('administration.inventory.addInventory')}
            onClickHandler={handleAddItemClickHandler}
            data-testid="add-inventory-button"
          />
        </Box>
      )}

      {viewState === EViewState.DETAILS ? (
        <Box data-testid="inventory-details-container">
          {renderDetailsView()}
        </Box>
      ) : (
        <>
          {/* Summary Statistics */}
          <Grid
            container
            spacing={{ xs: 1, sm: 2 }}
            sx={{ my: { xs: 2, sm: 4 } }}
            data-testid="inventory-summary-stats"
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'primary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-items-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {items.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.totalItems')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'secondary.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-value-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  ₹{(totalItemsValue / 100000).toFixed(1)}L
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.totalValue')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'success.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="active-categories-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {activeCategories}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.activeCategories')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="total-transactions-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {transactions.length}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.totalTransactions')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'warning.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="low-stock-items-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {lowStockItems}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.lowStockItems')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Box
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  bgcolor: 'error.light',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
                data-testid="pending-payments-card"
              >
                <Typography
                  variant="h6"
                  color="white"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                  }}
                >
                  {pendingPayments}
                </Typography>
                <Typography
                  variant="body2"
                  color="white"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  }}
                >
                  {translateText('administration.inventory.pendingPayments')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} data-testid="inventory-grids-container">
            <Grid item xs={12} data-testid="inventory-items-section">
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  mb: { xs: 1, sm: 2 },
                }}
              >
                {translateText('administration.inventory.inventoryItems')}
              </Typography>
              <InventoryGrid
                viewType="items"
                onRowClick={onItemsRowClick}
                isLoading={isLoading}
              />
            </Grid>

            <Grid item xs={12} data-testid="inventory-categories-section">
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  mb: { xs: 1, sm: 2 },
                }}
              >
                {translateText('administration.inventory.inventoryCategories')}
              </Typography>
              <InventoryGrid
                viewType="categories"
                onRowClick={onCategoriesRowClick}
                isLoading={isLoading}
              />
            </Grid>

            <Grid item xs={12} data-testid="inventory-transactions-section">
              <Typography
                variant="h5"
                gutterBottom
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                  mb: { xs: 1, sm: 2 },
                }}
              >
                {translateText(
                  'administration.inventory.inventoryTransactions'
                )}
              </Typography>
              <InventoryGrid
                viewType="transactions"
                onRowClick={onTransactionsRowClick}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </>
      )}
      <AddEditInventoryView />
    </Box>
  );
};

export default InventoryView;
